import { Component, inject, OnInit } from '@angular/core';
import { filter, first, Observable } from 'rxjs';
import { EnedisAccessRightModel, EnedisAccessRightStatus } from '../../enedis.models';
import { Store } from '@ngxs/store';
import {
    CreateAccessRight,
    DeleteAccessRight,
    RetryAccessRight,
    EnedisState,
    GetAllEnedisAccessRights,
    UnsetCurrentAccessRightId,
} from '../../enedis.state';
import {
    DisplayedColumns,
    DisplayType,
} from 'src/app/shared/components/ed-table/ed-table.component';
import {
    AlertService,
    DropdownMenuItem,
    LayerService,
    LayerSize,
    PopupService,
    PopupTypes,
} from '@ed---interne/ng-uui-components';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatTableDataSource } from '@angular/material/table';
import { User } from 'src/app/core/models/user.model';
import { AuthState } from 'src/app/core/states/auth.state';
import { EnedisAccessRightCreateComponent } from './access-right-create/enedis-access-right-create.component';
import { LayerPopupOptions } from 'src/app/shared/all.constants';
import { Router } from '@angular/router';

enum MeterMenuEvent {
    Access = 'Access',
    Delete = 'Delete',
    Retry = 'Retry',
    Restore = 'Restore',
}

interface AccessRightTableElement extends Partial<EnedisAccessRightModel> {
    actions: DropdownMenuItem[];
}

@UntilDestroy()
@Component({
    selector: 'app-enedis-dashboard',
    templateUrl: './enedis-dashboard.component.html',
    styleUrls: ['./enedis-dashboard.component.scss'],
})
export class EnedisComponent implements OnInit {
    public enedisAccessRights$: Observable<EnedisAccessRightModel[]> = inject(Store)
        .select(EnedisState.enedisAccessRights)
        .pipe(filter(Boolean));

    public isLoading = false;
    public isLoading$: Observable<boolean> = inject(Store).select(EnedisState.isLoading);

    loggedUser$: Observable<User | null> = inject(Store).select(AuthState.loggedUser);
    private loggedUser: User | undefined;

    public meterCounter = 0;
    public meterActiveCounter = 0;
    public meterPendingCounter = 0;
    public meterDisabledCounter = 0;
    public meterErrorCounter = 0;
    public meterExpiredCounter = 0;
    public meterRevokedCounter = 0;

    public meterActiveDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public meterPendingDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public meterExpiredDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public meterDisabledDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public meterErrorDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();
    public meterRevokedDataSource: MatTableDataSource<AccessRightTableElement> =
        new MatTableDataSource<AccessRightTableElement>();

    public meterActiveDisplayedColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'PDL',
            objectKey: 'pdlNumber',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Raison sociale',
            objectKey: 'clientName',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'SIRET',
            objectKey: 'clientSirenSiret',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nom',
            objectKey: 'clientRepresentativeName',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Email',
            objectKey: 'clientRepresentativeEmail',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Téléphone',
            objectKey: 'clientRepresentativePhone',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Création',
            objectKey: 'createdAt',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Validité',
            objectKey: 'validUntil',
            bold: false,
        },
    ];

    public meterErrorDisplayedColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'PDL',
            objectKey: 'pdlNumber',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Raison sociale',
            objectKey: 'clientName',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'SIRET',
            objectKey: 'clientSirenSiret',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nom',
            objectKey: 'clientRepresentativeName',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Email',
            objectKey: 'clientRepresentativeEmail',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Téléphone',
            objectKey: 'clientRepresentativePhone',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Création',
            objectKey: 'createdAt',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Erreur',
            objectKey: 'errorAt',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Message',
            objectKey: 'errorMessage',
            bold: false,
        },
    ];

    public meterDisabledDisplayedColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'PDL',
            objectKey: 'pdlNumber',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Raison sociale',
            objectKey: 'clientName',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'SIRET',
            objectKey: 'clientSirenSiret',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nom',
            objectKey: 'clientRepresentativeName',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Email',
            objectKey: 'clientRepresentativeEmail',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Téléphone',
            objectKey: 'clientRepresentativePhone',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Date de révocation',
            objectKey: 'disabledAt',
            bold: false,
        },
    ];

    constructor(
        private readonly store: Store,
        private router: Router,
        private readonly popupService: PopupService,
        private readonly alertService: AlertService,
        private readonly layerService: LayerService,
    ) {}

    public ngOnInit(): void {
        this.store.dispatch([new UnsetCurrentAccessRightId(), new GetAllEnedisAccessRights()]);

        this.loggedUser$.pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.loggedUser = data;
            if (this.loggedUser?.isAdmin) {
                const orgColumn = {
                    displayType: DisplayType.TEXT,
                    objectDisplayName: 'Organisation',
                    objectKey: 'organizationName',
                    bold: false,
                };
                this.meterActiveDisplayedColumns.unshift(orgColumn);
                this.meterDisabledDisplayedColumns.unshift(orgColumn);
                this.meterErrorDisplayedColumns.unshift(orgColumn);
            }
        });

        this.isLoading$!.pipe(untilDestroyed(this)).subscribe((loadingState) => {
            this.isLoading = loadingState;
        });

        this.enedisAccessRights$
            .pipe(untilDestroyed(this))
            .subscribe((enedisAccessRights: EnedisAccessRightModel[]) => {
                this.meterActiveDataSource = new MatTableDataSource<AccessRightTableElement>(
                    enedisAccessRights
                        .filter((ar) => ar.status === EnedisAccessRightStatus.ACTIVE)
                        .map((ar) => ({
                            ...ar,
                            organizationName: ar.organization.name,
                            actions: this.getActions(),
                        })),
                );

                this.meterPendingDataSource = new MatTableDataSource<AccessRightTableElement>(
                    enedisAccessRights
                        .filter((ar) => ar.status === EnedisAccessRightStatus.NEW)
                        .map((ar) => ({
                            ...ar,
                            organizationName: ar.organization.name,
                            actions: this.getActions(),
                        })),
                );

                this.meterErrorDataSource = new MatTableDataSource<AccessRightTableElement>(
                    enedisAccessRights
                        .filter((ar) => ar.status === EnedisAccessRightStatus.ERROR)
                        .map((ar) => ({
                            ...ar,
                            organizationName: ar.organization.name,
                            actions: [
                                {
                                    icon: 'icon-switch-horizontal-01',
                                    text: 'Reessayer',
                                    outputEventString: MeterMenuEvent.Retry,
                                },
                            ],
                        })),
                );
                this.meterRevokedDataSource = new MatTableDataSource<AccessRightTableElement>(
                    enedisAccessRights
                        .filter((ar) => ar.status === EnedisAccessRightStatus.REVOKED)
                        .map((ar) => ({
                            ...ar,
                            organizationName: ar.organization.name,
                            disabledAt: ar.revokedAt,
                            actions: [],
                        })),
                );

                this.meterExpiredDataSource = new MatTableDataSource<AccessRightTableElement>(
                    enedisAccessRights
                        .filter((ar) => ar.status === EnedisAccessRightStatus.EXPIRED)
                        .map((ar) => ({
                            ...ar,
                            organizationName: ar.organization.name,
                            actions: [],
                        })),
                );

                this.meterDisabledDataSource = new MatTableDataSource<AccessRightTableElement>(
                    enedisAccessRights
                        .filter((ar) => ar.status === EnedisAccessRightStatus.DISABLED)
                        .map((ar) => ({
                            ...ar,
                            organizationName: ar.organization.name,
                            actions: [
                                {
                                    icon: 'icon-flip-backward',
                                    text: 'Restaurer',
                                    outputEventString: MeterMenuEvent.Restore,
                                },
                            ],
                        })),
                );

                this.meterCounter = enedisAccessRights.length;
                this.meterActiveCounter = enedisAccessRights.filter(
                    (ar) => ar.status === EnedisAccessRightStatus.ACTIVE,
                ).length;
                this.meterPendingCounter = enedisAccessRights.filter(
                    (ar) => ar.status === EnedisAccessRightStatus.NEW,
                ).length;
                this.meterDisabledCounter = enedisAccessRights.filter(
                    (ar) => ar.status === EnedisAccessRightStatus.DISABLED,
                ).length;
                this.meterErrorCounter = enedisAccessRights.filter(
                    (ar) => ar.status === EnedisAccessRightStatus.ERROR,
                ).length;
                this.meterRevokedCounter = enedisAccessRights.filter(
                    (ar) => ar.status === EnedisAccessRightStatus.REVOKED,
                ).length;
            });
    }

    private getActions(): DropdownMenuItem[] {
        const actions: DropdownMenuItem[] = [
            {
                icon: 'icon-eye',
                text: 'Accéder',
                outputEventString: MeterMenuEvent.Access,
            },
            {
                icon: 'icon-trash-01',
                text: 'Révoquer',
                outputEventString: MeterMenuEvent.Delete,
            },
        ];

        return actions;
    }
    public retryAccessRight(accessRight: EnedisAccessRightModel): void {
        this.popupService
            .openPopup({
                title: "Renouvellement de l'accès",
                htmlContent: `Confirmez-vous vouloir réactiver l'accès au PRM <b>${accessRight.pdlNumber}</b> ?`,
                confirmButtonText: 'Réactiver',
                type: PopupTypes.Info,
            })
            .then((result) => {
                if (result.isConfirmed) {
                    this.store
                        .dispatch(new RetryAccessRight({ accessRightId: accessRight?.id }))
                        .pipe(untilDestroyed(this))
                        .subscribe({
                            next: () => {
                                this.alertService.valid(
                                    'Restauration',
                                    "L'accès a bien été restauré",
                                );
                            },
                            error: (err) => {
                                this.alertService.error(
                                    'Restauration',
                                    "une erreur est survenue : Impossible de restaurer l'accès",
                                );
                            },
                        });
                }
            });
    }
    public deleteAccessRight(accessRight: EnedisAccessRightModel): void {
        this.popupService
            .openPopup({
                title: "Révocation de l'accès",
                htmlContent: `Confirmez-vous la révocation de l'accès au PRM <b>${accessRight.pdlNumber}</b> ?`,
                confirmButtonText: 'Révoquer',
                type: PopupTypes.Delete,
            })
            .then((result) => {
                if (result.isConfirmed) {
                    this.store
                        .dispatch(new DeleteAccessRight({ accessRightId: accessRight?.id }))
                        .pipe(untilDestroyed(this))
                        .subscribe({
                            next: () => {
                                this.alertService.valid('Révocation', "L'accès a bien été révoqué");
                            },
                            error: (err) => {
                                this.alertService.error(
                                    'Révocation',
                                    "une erreur est survenue : Impossible de révoquer l'accès",
                                );
                            },
                        });
                }
            });
    }

    public unDeleteAccessRight(accessRight: EnedisAccessRightModel): void {
        if (new Date(accessRight.validUntil).getTime() < new Date().getTime()) {
            this.alertService.error(
                'Réstauration',
                'Vous ne pouvez pas restaurer un accès dont la date de péremption est dépassée.',
            );
            return;
        }

        this.popupService
            .openPopup({
                title: "Récupération de l'accès",
                htmlContent: `Confirmez-vous la récupération de l'accès au PRM <b>${accessRight.pdlNumber}</b> ?`,
                confirmButtonText: 'Récupérer',
                type: PopupTypes.Info,
            })
            .then((result) => {
                if (result.isConfirmed) {
                    this.store
                        .dispatch(new CreateAccessRight({ accessRight }))
                        .pipe(untilDestroyed(this))
                        .subscribe({
                            next: () => {
                                this.alertService.valid(
                                    'Réstauration',
                                    "L'accès a bien été restauré",
                                );
                            },
                            error: (err) => {
                                this.alertService.error(
                                    'Réstauration',
                                    "une erreur est survenue : Impossible de restaurer l'accès",
                                );
                            },
                        });
                }
            });
    }

    public onDropdownMenuClick(enedisAccessRight: EnedisAccessRightModel, event: string): void {
        switch (event) {
            case MeterMenuEvent.Access:
                this.goToDetail(enedisAccessRight.id);
                break;
            case MeterMenuEvent.Delete:
                return this.deleteAccessRight(enedisAccessRight);
            case MeterMenuEvent.Restore:
                return this.unDeleteAccessRight(enedisAccessRight);
            case MeterMenuEvent.Retry:
                return this.retryAccessRight(enedisAccessRight);
            default:
                break;
        }
    }

    public async goToDetail(accessRightId: string): Promise<void> {
        this.router.navigate(['/main/enedis/' + accessRightId + '/informations']);
    }

    public openModalCreateAccessRight(): void {
        const header = "Déclarer un nouveau droit d'accès";
        this.layerService.show(
            EnedisAccessRightCreateComponent,
            { header },
            { size: LayerSize.Large, withClosePopup: LayerPopupOptions.Creating },
        );
    }
}
